import { useEffect, useRef, useState } from 'react';
import { Icon } from './icons.tsx';
import { AFFIDAVIT_FIELD_LABELS } from '@civilos/domain';
import { api } from './api.ts';
import { useAuth } from './auth.tsx';
import { fmtDateTime } from './format.ts';
import { Badge, Card, ErrorBox, Field, Notice, useAction } from './ui.tsx';
import { confirmWithFingerprint, useHasPasskey } from './passkey.tsx';

const setupLink = () => (location.pathname.startsWith('/field') ? '/field/security' : '/me');

/** Finger/stylus signature pad → PNG data URL (AFF-003). */
export function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  useEffect(() => {
    const c = ref.current!;
    const ctx = c.getContext('2d')!;
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111';
    const pos = (e: PointerEvent) => {
      const r = c.getBoundingClientRect();
      return [((e.clientX - r.left) * c.width) / r.width, ((e.clientY - r.top) * c.height) / r.height] as const;
    };
    const down = (e: PointerEvent) => {
      drawing.current = true;
      c.setPointerCapture(e.pointerId);
      const [x, y] = pos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const move = (e: PointerEvent) => {
      if (!drawing.current) return;
      const [x, y] = pos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
      dirty.current = true;
    };
    const up = () => {
      drawing.current = false;
      if (dirty.current) onChange(c.toDataURL('image/png'));
    };
    c.addEventListener('pointerdown', down);
    c.addEventListener('pointermove', move);
    c.addEventListener('pointerup', up);
    return () => {
      c.removeEventListener('pointerdown', down);
      c.removeEventListener('pointermove', move);
      c.removeEventListener('pointerup', up);
    };
  }, [onChange]);
  return (
    <div className="sigpad">
      <canvas ref={ref} width={600} height={160} aria-label="Signature pad" />
      <button
        type="button"
        className="btn sm ghost"
        onClick={() => {
          const c = ref.current!;
          c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
          dirty.current = false;
          onChange(null);
        }}
      >
        Clear
      </button>
    </div>
  );
}

/** AFF-001 … AFF-007: generate, preview, correct, sign, amend. Shared by office and field. */
export function AffidavitPanel({ paperId, paperStatus, affidavits, onChanged }: { paperId: string; paperStatus: string; affidavits: any[]; onChanged: () => void }) {
  const { me, can } = useAuth();
  const draft = affidavits.find((a) => a.status === 'draft');
  const signed = [...affidavits].reverse().find((a) => a.status === 'signed');
  const a = useAction();
  const [edit, setEdit] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState(false);
  const [method, setMethod] = useState<'typed' | 'drawn' | 'saved'>('typed');
  const [mySig, setMySig] = useState<any>(null);
  const [typedName, setTypedName] = useState(me?.user.displayName ?? '');
  const [certify, setCertify] = useState(false);
  const [sig, setSig] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [full, setFull] = useState<any>(null);
  const hasFp = useHasPasskey();
  const fpRequired = !!me?.agency.requireFingerprintToSign;
  const [fpChoice, setFpChoice] = useState<boolean | null>(null);
  const useFp = fpRequired || (fpChoice ?? !!hasFp);

  useEffect(() => {
    if (draft) api.get(`/api/affidavits/${draft.id}`).then((r) => setFull(r.affidavit)).catch(() => undefined);
    else setFull(null);
  }, [draft?.id, previewKey]);

  useEffect(() => {
    if (!draft) return;
    api
      .get('/api/me/signature')
      .then((r) => {
        setMySig(r);
        if (r.hasSignature && me!.agency.signatureMethods.includes('drawn')) setMethod('saved');
      })
      .catch(() => undefined);
  }, [draft?.id]);

  const canGenerate = ['SERVED', 'NON_SERVED'].includes(paperStatus) && !draft && !signed;
  const notarize = (x: any) => {
    if (!window.confirm(`Notarize the affidavit signed by ${x.signed_by_name ?? 'the deputy'}? Only notarize when the deputy has sworn to it before you.`)) return;
    a.run(async () => {
      const fingerprint = hasFp ? await confirmWithFingerprint(`affidavit.notarize:${x.id}`) : undefined;
      await api.post(`/api/affidavits/${x.id}/notarize`, { fingerprint });
      onChanged();
    });
  };

  return (
    <Card title="Affidavit / return">
      <div className="stack">
        {affidavits.length > 0 && (
          <ul className="plain">
            {affidavits.map((x) => (
              <li key={x.id} className="row gap wrap">
                <span>v{x.version}</span>
                <Badge tone={x.status === 'signed' ? 'ok' : x.status === 'draft' ? 'info' : 'neutral'}>{x.status}</Badge>
                <span className="muted small">{x.outcome.replace('_', '-')}</span>
                {x.signed_at && <span className="muted small">signed {fmtDateTime(x.signed_at)}{x.signed_by_name ? ` by ${x.signed_by_name}` : ''}</span>}
                {x.notarized_at ? (
                  <Badge tone="ok">notarized {fmtDateTime(x.notarized_at)}{x.notarized_by_name ? ` · ${x.notarized_by_name}` : ''}</Badge>
                ) : x.status === 'signed' ? (
                  can('affidavits.notarize') && x.signed_by !== me?.user.id ? (
                    fpRequired && hasFp === false ? (
                      <a className="btn sm" href={setupLink()}>
                        Set up fingerprint to notarize
                      </a>
                    ) : (
                      <button className="btn sm primary" onClick={() => notarize(x)} disabled={a.busy}>
                        {hasFp ? <><Icon name="fingerprint" size={16} /> Notarize with fingerprint</> : 'Notarize'}
                      </button>
                    )
                  ) : (
                    <Badge tone="warn">waiting for notary</Badge>
                  )
                ) : null}
                <a className="btn sm" href={`/api/affidavits/${x.id}/pdf`} target="_blank" rel="noreferrer">
                  View PDF
                </a>
                {x.status === 'signed' && (
                  <a className="btn sm" href={`/api/affidavits/${x.id}/pdf?download=1`}>
                    Download
                  </a>
                )}
                {x.status === 'signed' && (can('affidavits.amend') || can('affidavits.sign')) && !draft && (
                  <button
                    className="btn sm ghost"
                    disabled={a.busy}
                    onClick={() =>
                      a.run(async () => {
                        await api.post(`/api/affidavits/${x.id}/amend`);
                        onChanged();
                      })
                    }
                  >
                    Amend
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {canGenerate && (
          <button
            className="btn primary"
            disabled={a.busy}
            onClick={() =>
              a.run(async () => {
                await api.post(`/api/papers/${paperId}/affidavit`);
                onChanged();
              })
            }
          >
            Generate affidavit
          </button>
        )}
        {!['SERVED', 'NON_SERVED'].includes(paperStatus) && !affidavits.length && <p className="muted small">The affidavit becomes available once the paper is served or validly non-served.</p>}

        {draft && full && (
          <div className="stack">
            {full.supersedes_id && <Notice tone="warn">This is an amendment. Signing it supersedes the earlier affidavit and the customer will receive the amendment.</Notice>}
            <div className="aff-preview">
              <div className="aff-title">{full.rendered.title}</div>
              {full.rendered.paragraphs.map((p: string, i: number) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            {full.rendered.missingRequired.length > 0 && (
              <Notice tone="warn">Complete before signing: {full.rendered.missingRequired.map((k: string) => (AFFIDAVIT_FIELD_LABELS as any)[k]).join(', ')}</Notice>
            )}
            <div className="row gap wrap">
              <a className="btn sm" href={`/api/affidavits/${draft.id}/pdf?v=${previewKey}`} target="_blank" rel="noreferrer">
                Preview PDF
              </a>
              <button className="btn sm" onClick={() => setEditing(!editing)}>
                {editing ? 'Close corrections' : 'Correct affidavit fields'}
              </button>
            </div>
            {editing && (
              <form
                className="stack"
                onSubmit={(e) => {
                  e.preventDefault();
                  a.run(async () => {
                    await api.patch(`/api/affidavits/${draft.id}`, { corrections: edit });
                    setEdit({});
                    setEditing(false);
                    setPreviewKey((k) => k + 1);
                  });
                }}
              >
                <p className="small muted">Corrections change only the affidavit text and are logged. The underlying service record is not altered.</p>
                <div className="grid2">
                  {Object.entries(AFFIDAVIT_FIELD_LABELS).map(([k, label]) => {
                    const current = edit[k] ?? full.corrections.filter((c: any) => c.field === k).at(-1)?.to ?? full.generated_fields[k] ?? '';
                    return (
                      <Field key={k} label={label}>
                        {k === 'attemptsSummary' ? (
                          <textarea rows={3} value={current} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />
                        ) : (
                          <input value={current} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />
                        )}
                      </Field>
                    );
                  })}
                </div>
                <button className="btn" disabled={a.busy || !Object.keys(edit).length}>
                  Save corrections
                </button>
              </form>
            )}
            {can('affidavits.sign') && (!full.generated_fields.affiantUserId || full.generated_fields.affiantUserId === me?.user.id) ? (
              <form
                className="stack sign-box"
                onSubmit={(e) => {
                  e.preventDefault();
                  a.run(async () => {
                    const fingerprint = useFp ? await confirmWithFingerprint(`affidavit.sign:${draft.id}`) : undefined;
                    await api.post(`/api/affidavits/${draft.id}/sign`, { method, typedName, certify, imageDataUrl: method === 'drawn' ? sig : undefined, fingerprint });
                    onChanged();
                  });
                }}
              >
                <strong>Sign</strong>
                <div className="row gap">
                  {me!.agency.signatureMethods.includes('typed') && (
                    <label className="row gap-sm">
                      <input type="radio" checked={method === 'typed'} onChange={() => setMethod('typed')} /> Typed e-signature
                    </label>
                  )}
                  {me!.agency.signatureMethods.includes('drawn') && mySig?.hasSignature && (
                    <label className="row gap-sm">
                      <input type="radio" checked={method === 'saved'} onChange={() => setMethod('saved')} /> Use my saved signature
                    </label>
                  )}
                  {me!.agency.signatureMethods.includes('drawn') && (
                    <label className="row gap-sm">
                      <input type="radio" checked={method === 'drawn'} onChange={() => setMethod('drawn')} /> Draw signature
                    </label>
                  )}
                </div>
                {method === 'saved' ? (
                  <div className="mysig-img small-sig">
                    <img src={mySig.imageDataUrl} alt="Your saved signature" />
                  </div>
                ) : method === 'typed' ? (
                  <Field label="Typed name">
                    <input value={typedName} onChange={(e) => setTypedName(e.target.value)} />
                  </Field>
                ) : (
                  <SignaturePad onChange={setSig} />
                )}
                <label className="row gap-sm">
                  <input type="checkbox" checked={certify} onChange={(e) => setCertify(e.target.checked)} /> I certify this affidavit is true and correct.
                </label>
                {hasFp && !fpRequired && (
                  <label className="row gap-sm">
                    <input type="checkbox" checked={useFp} onChange={(e) => setFpChoice(e.target.checked)} /> Confirm it’s me with my fingerprint
                  </label>
                )}
                {fpRequired && hasFp === false && (
                  <Notice tone="warn">
                    Your agency requires your fingerprint to sign. <a href={setupLink()}>Set up fingerprint sign-in</a> on this device first.
                  </Notice>
                )}
                <button className="btn primary" disabled={a.busy || !certify || (method === 'drawn' && !sig) || full.rendered.missingRequired.length > 0 || (fpRequired && !hasFp)}>
                  {useFp ? (a.busy ? 'Touch the fingerprint reader…' : <><Icon name="fingerprint" size={16} /> Sign with fingerprint and finalize</>) : 'Sign and finalize'}
                </button>
                <span className="small muted">Once signed the affidavit is locked. Corrections require an amended affidavit.</span>
              </form>
            ) : (
              <Notice>Awaiting signature by the serving officer.</Notice>
            )}
          </div>
        )}
        <ErrorBox error={a.error} />
      </div>
    </Card>
  );
}
